import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import CancelIcon from "@mui/icons-material/Cancel";
import CommentIcon from "@mui/icons-material/Comment";
import { DocumentItemStyles } from "./GenericOrderInfoStyles";
import EditIcon from "@mui/icons-material/Edit";
import { DeletePopup } from "@Components/Popup/DeletePopup";

import {
  inputTypes,
  approvalNames,
  approvalStates,
} from "../../Utils/HelpingObjects/HelpingObjects";
import { useState } from "react";
import { EditSectionPopup } from "../GenericForm/EditSectionPopup";
import useSendPutRequest from "@Hooks/useSendPutRequest";
import { APPROVALS_URL, ACID_PERMITTED_URL } from "../../API/APIRoutes";
import axiosInstance from "@API/axios";
import { useDispatch, useSelector } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import axios from "axios";
import { useEffect } from "react";
import { formatDateUTC } from "../../Utils/Validations/HelperFunctions/FormaterFunctions";
import useDeleteItem from "../../Hooks/useDeleteItem";
import DeleteIcon from "@mui/icons-material/Delete";
import MeatballsMenu from "@Components/MeatballsMenu/MeatballsMenu";

export default function ApprovalList({
  orderId,
  approvals: initialApprovals,
  selectedObject,
  setSelectedObject,
  editable,
}) {
  const [approvals, setApprovals] = useState(initialApprovals || []);
  const [acidPermitted, setAcidPermitted] = useState(false);
  const [acidLoading, setAcidLoading] = useState(true);
  const [acidSaving, setAcidSaving] = useState(false);
  const [acidLoadError, setAcidLoadError] = useState(false);
  const dispatch = useDispatch();
  const canEditAcidPermitted = useSelector((state) =>
    state.user.roles?.includes("SYASAT")
  );
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const { requestMethod: putItem } =
    useSendPutRequest();

  const [PutItemLoading, setPutItemLoading] = useState(false);
  useEffect(() => {
    setApprovals(initialApprovals || []);
  }, [initialApprovals]);

  useEffect(() => {
    let active = true;
    setAcidLoading(true);
    setAcidLoadError(false);
    axiosInstance
      .get(ACID_PERMITTED_URL(orderId))
      .then(({ data }) => {
        if (active) setAcidPermitted(data.acidPermitted === true);
      })
      .catch(() => {
        if (active) setAcidLoadError(true);
      })
      .finally(() => {
        if (active) setAcidLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderId]);

  const updateAcidPermitted = async (value) => {
    if (!canEditAcidPermitted) return;
    setAcidSaving(true);
    try {
      await axiosInstance.put(ACID_PERMITTED_URL(orderId), {
        acidPermitted: value,
      });
      setAcidPermitted(value);
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message: "Failed to update ACID permission.",
          error: true,
        })
      );
    } finally {
      setAcidSaving(false);
    }
  };

  const formFields = [
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "approvalName",
      label: "Approval Name",
      options: approvalNames,
      editable: false,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "approvalState",
      label: "Approval State",
      options: approvalStates,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.DATE,
      key: "submissionDate",
      label: "Submission Date",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.DATE,
      key: "receivedDate",
      label: "Received Date",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.TEXT,
      key: "comment",
      label: "Comment",
      editable: true,
    },
  ];

  const onEditApproval = (approval) => {
    setSelectedApproval(approval);
    setShowEditPopup(true);
  };
  const closeEditPopup = () => setShowEditPopup(false);

  const confirmEdit = async (newApprovalData, file) => {
    setPutItemLoading(true);
    const processedData = {
      ...newApprovalData,
      receivedDate: formatDateUTC(newApprovalData.receivedDate),
      submissionDate: formatDateUTC(newApprovalData.submissionDate),
      isFileAttached: file ? true : false,
      orderId: Number(orderId), // Convert orderId to number
    };

    const response = await putItem(
      APPROVALS_URL,
      processedData,
      `Approval Edited Successfully!`,
      `Failed To Edit Approval`
    );
    // Extract preSignedUrl and objectKey outside the .then()
    if (file && response?.data?.preSignedUrl) {
      const { preSignedUrl, objectKey } = response.data;
      // Upload file to Minio
      await axios.put(preSignedUrl, file, {
        headers: {
          "Content-Type": "application/pdf",
        },
      });
    }
    // Update the local approvals array with the edited approval
    const updatedApprovals = approvals.map((approval) => {
      if (
        approval.id === newApprovalData.id ||
        approval.approvalName === newApprovalData.approvalName
      ) {
        return processedData;
      }
      return approval;
    });

    setApprovals(updatedApprovals);
    // Refresh the page to show the updated approval
    window.location.href = window.location.href;
    closeEditPopup();
  };
  //**************************** Delete Approval ****************************/
  const { loading: deleteItemLoading, deleteItem } = useDeleteItem();
  const [showDeleteApprovalPopup, setShowDeleteApprovalPopup] = useState(false);
  const [selectedApprovalToDelete, setSelectedApprovalToDelete] =
    useState(null);

  const openDeleteApprovalPopup = (approval) => {
    setSelectedApprovalToDelete(approval);
    setShowDeleteApprovalPopup(true);
  };
  const closeDeleteApprovalPopup = () => {
    setShowDeleteApprovalPopup(false);
  };
  const confirmDeleteApproval = async (approvalId) => {
    await deleteItem(
      approvalId,
      closeDeleteApprovalPopup,
      APPROVALS_URL,
      `Approval Deleted Successfully!`,
      `Failed to delete Approval!`
    );
    // Remove the deleted approval from the local state
    const updatedApprovals = approvals.filter(
      (approval) => approval.id !== approvalId
    );
    setApprovals(updatedApprovals);
    window.location.reload();
    closeDeleteApprovalPopup();
  };
  //**************************** MeatballsMenu Options ****************************/
  const meatballsMenuOptions = (approval) => [
    {
      label: "Edit Approval",
      icon: <EditIcon />,
      color: "primary",
      action: () => onEditApproval(approval),
      showOption: editable,
    },
    {
      label: "Delete Approval",
      icon: <DeleteIcon />,
      color: "error",
      action: () => openDeleteApprovalPopup(approval),
      showOption: editable,
    },
  ];
  return (
    <Box sx={{ maxHeight: "85vh", overflowY: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <FormControlLabel
          sx={{ "& .MuiFormControlLabel-label.Mui-disabled": { color: "text.primary" } }}
          control={
            <Checkbox
              checked={acidPermitted}
              onChange={(event) => updateAcidPermitted(event.target.checked)}
              disabled={
                !canEditAcidPermitted || acidLoading || acidSaving || acidLoadError
              }
            />
          }
          label="ACID Permitted"
        />
        {(acidLoading || acidSaving) && <CircularProgress size={18} />}
      </Box>
      {acidLoadError && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          Could not load ACID permission.
        </Typography>
      )}
      {approvals.map((approval, idx) => {
        const approvalId =
          approval.objectKey || `${approval.approvalName}-${idx}`;
        const isSelected = selectedObject?._approvalId === approvalId;

        return (
          <>
            <Box
              key={approvalId}
              onClick={() =>
                setSelectedObject({ ...approval, _approvalId: approvalId })
              }
              sx={{
                ...DocumentItemStyles(isSelected),
                position: "relative", // Add position relative to container
              }}
            >
              {/* Meatballs menu for invoice actions */}
              {editable && (
                <Box
                  sx={{ position: "absolute", right: 10, top: 10 }}
                  onClick={(e) => e.stopPropagation()} // Stop propagation to prevent invoice selection
                >
                  <MeatballsMenu options={meatballsMenuOptions(approval)} />
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  maxWidth: 400,
                  width: "100%",
                }}
              >
                <Typography variant="subtitle1">
                  Name: {approval.approvalName.replace(/_/g, " ")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0 }}></Typography>
                  {approval.approvalState === "APPROVED" && (
                    <CheckCircleIcon color="success" fontSize="small" />
                  )}
                  {approval.approvalState === "PENDING" && (
                    <PauseCircleFilledIcon
                      color="warning"
                      fontSize="small"
                      sx={{ mb: 0.2 }}
                    />
                  )}
                  {approval.approvalState === "REJECTED" && (
                    <CancelIcon color="error" fontSize="small" />
                  )}
                  {approval.approvalState}
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Submitted: {new Date(approval.submissionDate).toLocaleDateString()}
              </Typography>
              <br/>
              <Typography variant="caption" color="text.secondary">
                Received: {new Date(approval.receivedDate).toLocaleDateString()}
              </Typography>

              {approval.comment && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mt: 1,
                    flexWrap: "nowrap",
                  }}
                >
                  <CommentIcon sx={{ color: "text.secondary" }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    Comment:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontStyle="italic"
                    sx={{
                      overflowWrap: "anywhere",
                      flex: 1,
                    }}
                  >
                    {approval.comment}
                  </Typography>
                </Box>
              )}
            </Box>
            <hr />
          </>
        );
      })}
      {EditSectionPopup(
        "Approval",
        showEditPopup,
        closeEditPopup,
        confirmEdit,
        PutItemLoading,
        formFields,
        selectedApproval,
        true
      )}
      {DeletePopup(
        "Approval",
        showDeleteApprovalPopup,
        closeDeleteApprovalPopup,
        () => confirmDeleteApproval(selectedApprovalToDelete?.id),
        null,
        deleteItemLoading
      )}
    </Box>
  );
}
